import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { API } from "../lib/api";

const uid = () => `s-${Math.random().toString(36).slice(2)}`;

export const Chatbot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Namaste. I am the Astrohomeopathy assistant. Ask me about planets, remedies, or Dr. Misra's approach." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(uid());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setLoading(true);

    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data:")) continue;
          try {
            const data = JSON.parse(p.slice(5).trim());
            if (data.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + data.delta };
                return copy;
              });
            }
            if (data.done) break;
            if (data.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: "Sorry, I had trouble reaching the oracle. Please try again." };
                return copy;
              });
            }
          } catch {
            // ignore parse errors
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Connection error. Please try again." };
        return copy;
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="chatbot-toggle"
        className={`fixed bottom-24 right-6 z-40 w-14 h-14 rounded-full bg-gradient-to-br from-[#0EA5E9] to-[#D4AF37] text-white flex items-center justify-center shadow-[0_10px_30px_-5px_rgba(14,165,233,0.5)] hover:scale-110 transition-transform ${open ? "hidden" : ""}`}
        aria-label="Open chat"
      >
        <MessageCircle size={22} />
      </button>

      {open && (
        <div
          data-testid="chatbot-panel"
          className="fixed bottom-24 right-6 z-40 w-[calc(100%-3rem)] sm:w-96 h-[560px] max-h-[75vh] rounded-3xl bg-white shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
        >
            <div className="p-4 bg-gradient-to-br from-[#0EA5E9] to-[#0284C7] text-white flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center overflow-hidden">
                <img src="https://customer-assets.emergentagent.com/job_astrohomeo-pro/artifacts/xtgzo2ot_121471.jpg" alt="Astrohomeopathy®" className="w-full h-full object-contain" />
              </div>
              <div>
                <div className="font-semibold text-sm">Astrohomeopathy AI</div>
                <div className="text-[11px] opacity-80">Powered by Dr. Misra&rsquo;s system</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chatbot-close" aria-label="Close">
              <X size={20} />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#F0F9FF]">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#0F172A] text-white rounded-br-sm"
                      : "bg-white border border-slate-200 text-[#0F172A] rounded-bl-sm"
                  }`}
                >
                  {m.content || <span className="inline-flex gap-1"><span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-pulse" /><span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-pulse [animation-delay:150ms]" /><span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-pulse [animation-delay:300ms]" /></span>}
                </div>
              </div>
            ))}
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="p-3 border-t border-slate-100 bg-white flex gap-2"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask a question..."
              data-testid="chatbot-input"
              disabled={loading}
              className="flex-1 rounded-full border border-slate-200 px-4 py-2 text-sm focus:outline-none focus:border-[#D4AF37]"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              data-testid="chatbot-send"
              className="w-10 h-10 rounded-full bg-[#D4AF37] text-white flex items-center justify-center hover:bg-[#B5952F] transition disabled:opacity-50"
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      )}
    </>
  );
};
